import {
	computed,
	ref, shallowRef, watch,
} from "vue";

import {
	CalendarDate, DateFormatter, getLocalTimeZone,
} from "@internationalized/date";
import { defineStore } from "pinia";

type DatePreset = "last-7" | "last-30" | "last-90" | "last-180" | "last-365" | "custom";

const presetDayMap: Record<Exclude<DatePreset, "custom">, number> = {
	"last-7": 7,
	"last-30": 30,
	"last-90": 90,
	"last-180": 180,
	"last-365": 365,
};

const toCalendarDate = (date: Date) => new CalendarDate(
	date.getFullYear(),
	date.getMonth() + 1,
	date.getDate(),
);

export const useDateStore = defineStore("dates", () => {
	const until = ref(new Date());
	const since = ref(new Date(until.value.getTime() - 1000 * 60 * 60 * 24 * 365));
	const preset = ref<DatePreset>("last-365");
	const dateRange = shallowRef({
		start: toCalendarDate(since.value),
		end: toCalendarDate(until.value),
	});

	const setPresetRange = (days: number) => {
		until.value = new Date();
		since.value = new Date(until.value.getTime() - 1000 * 60 * 60 * 24 * days);
		dateRange.value = {
			start: toCalendarDate(since.value),
			end: toCalendarDate(until.value),
		};
	};

	watch(preset, (newPreset) => {
		if (newPreset === "custom") {
			return;
		}
		setPresetRange(presetDayMap[newPreset]);
	}, { flush: "sync" });

	watch(dateRange, (newDateRange) => {
		if (preset.value !== "custom") {
			return;
		}
		const tz = getLocalTimeZone();
		since.value = newDateRange.start.toDate(tz);
		until.value = new Date(newDateRange.end.add({ days: 1 }).toDate(tz).getTime() - 1);
	}, { flush: "sync" });

	const reset = () => {
		if (preset.value === "last-365") {
			setPresetRange(365);
		} else {
			preset.value = "last-365";
		}
	};

	const df = new DateFormatter("en-US", {
		dateStyle: "short",
	});
	const presetOptions = computed(() => {
		const opts: {
			label: string;
			value: DatePreset;
		}[] = [
			{
				label: "Last 7 days",
				value: "last-7",
			},
			{
				label: "Last 30 days",
				value: "last-30",
			},
			{
				label: "Last 90 days",
				value: "last-90",
			},
			{
				label: "Last 180 days",
				value: "last-180",
			},
			{
				label: "Last 365 days",
				value: "last-365",
			},
			{
				label: preset.value === "custom" ? `${df.format(dateRange.value.start.toDate(getLocalTimeZone()))} -
                ${df.format(dateRange.value.end.toDate(getLocalTimeZone()))}` : "Custom",
				value: "custom",
			},
		];
		return opts;
	});

	return {
		since,
		until,
		dateRange,
		preset,
		presetOptions,
		reset,
	};
});
